import { useState, useEffect } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import { Switch } from "components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "components/ui/card";
// import { Badge } from "components/ui/badge";
// import { Separator } from "components/ui/separator";
import {
  Brain,
  Plus,
  Trash,
  FloppyDisk,
  SpinnerGap,
  Warning,
} from "@phosphor-icons/react";

export enum AIProvider {
  OPENAI = "openai",
  CLAUDE = "claude",
}

export interface ExtractionField {
  key: string;
  label: string;
  type: "string" | "date" | "number" | "boolean" | "enum";
  required: boolean;
  enumValues?: string[];
  extractionHint?: string;
}

export interface ValidityRule {
  type: "expiration_check" | "field_required" | "field_value" | "field_not_contains" | "custom";
  field?: string;
  operator?: "==" | "!=" | ">" | "<" | ">=" | "<=" | "contains" | "not_contains" | "not_empty" | "is_empty";
  value?: any;
  errorMessage: string;
}

export interface ValidityRuleGroup {
  type: "AND" | "OR";
  rules: (ValidityRule | ValidityRuleGroup)[];
}

export interface AnalysisConfig {
  labelId: number;
  label: string;
  aiAnalysisEnabled: boolean;
  documentDescription: string | null;
  extractionPrompt: string | null;
  extractionFields: ExtractionField[] | null;
  validityRules: ValidityRuleGroup | null;
  conformityRules: ValidityRuleGroup | null;
  aiProvider: AIProvider;
  aiModel: string;
  naturalLanguageAliases: string[] | null;
}

interface LabelAnalysisConfigPanelProps {
  labelId: number;
  labelName: string;
  isOpen: boolean;
  onClose: () => void;
}

const AI_MODELS = {
  [AIProvider.OPENAI]: [
    { value: "gpt-4o", label: "GPT-4o (Aanbevolen)" },
    { value: "gpt-4o-mini", label: "GPT-4o Mini (Goedkoper)" },
    { value: "gpt-4-turbo", label: "GPT-4 Turbo" },
  ],
  [AIProvider.CLAUDE]: [
    { value: "claude-sonnet-4-20250514", label: "Claude Sonnet 4 (Aanbevolen)" },
    { value: "claude-3-5-sonnet-20241022", label: "Claude 3.5 Sonnet" },
    { value: "claude-3-haiku-20240307", label: "Claude 3 Haiku (Goedkoper)" },
  ],
};

const FIELD_TYPES = [
  { value: "string", label: "Tekst" },
  { value: "date", label: "Datum" },
  { value: "number", label: "Getal" },
  { value: "boolean", label: "Ja/Nee" },
  { value: "enum", label: "Keuzelijst" },
];

const LabelAnalysisConfigPanel = ({
  labelId,
  labelName,
  isOpen,
  onClose,
}: LabelAnalysisConfigPanelProps) => {
  const { session } = useSession();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [config, setConfig] = useState<AnalysisConfig | null>(null);

  // Local state for form editing
  const [aiAnalysisEnabled, setAiAnalysisEnabled] = useState(false);
  const [aiProvider, setAiProvider] = useState<AIProvider>(AIProvider.OPENAI);
  const [aiModel, setAiModel] = useState("gpt-4o");
  const [documentDescription, setDocumentDescription] = useState("");
  const [extractionPrompt, setExtractionPrompt] = useState("");
  const [extractionFields, setExtractionFields] = useState<ExtractionField[]>([]);
  const [synonyms, setSynonyms] = useState<string[]>([]);
  const [newSynonym, setNewSynonym] = useState("");

  // Fetch current config
  useEffect(() => {
    if (isOpen && labelId) {
      fetchConfig();
    }
  }, [isOpen, labelId]);

  const fetchConfig = async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/file/label/${labelId}/analysis-config`,
        { method: "GET" },
        session
      );

      if (response.ok) {
        const data: AnalysisConfig = await response.json();
        setConfig(data);
        setAiAnalysisEnabled(data.aiAnalysisEnabled);
        setAiProvider(data.aiProvider || AIProvider.OPENAI);
        setAiModel(data.aiModel || "gpt-4o");
        setDocumentDescription(data.documentDescription || "");
        setExtractionPrompt(data.extractionPrompt || "");
        setExtractionFields(data.extractionFields || []);
        setSynonyms(data.naturalLanguageAliases || []);
      } else {
        toast.error("Fout bij ophalen configuratie");
      }
    } catch (error) {
      console.error("Error fetching analysis config:", error);
      toast.error("Fout bij ophalen configuratie");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updateData = {
        aiAnalysisEnabled,
        aiProvider,
        aiModel,
        documentDescription: documentDescription || null,
        extractionPrompt: extractionPrompt || null,
        extractionFields: extractionFields.length > 0 ? extractionFields : null,
        validityRules: config?.validityRules || null,
        conformityRules: config?.conformityRules || null,
        naturalLanguageAliases: synonyms.length > 0 ? synonyms : [],
      };

      const response = await fetchWithAuth(
        `${API_URL}/file/label/${labelId}/analysis-config`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updateData),
        },
        session
      );

      if (response.ok) {
        toast.success("AI analyse configuratie opgeslagen");
        onClose();
      } else {
        toast.error("Fout bij opslaan configuratie");
      }
    } catch (error) {
      console.error("Error saving analysis config:", error);
      toast.error("Fout bij opslaan configuratie");
    } finally {
      setIsSaving(false);
    }
  };

  const addExtractionField = () => {
    setExtractionFields([
      ...extractionFields,
      {
        key: "",
        label: "",
        type: "string",
        required: false,
        extractionHint: "",
      },
    ]);
  };

  const updateExtractionField = (index: number, field: Partial<ExtractionField>) => {
    const updated = [...extractionFields];
    updated[index] = { ...updated[index], ...field };
    setExtractionFields(updated);
  };

  const removeExtractionField = (index: number) => {
    setExtractionFields(extractionFields.filter((_, i) => i !== index));
  };

  const addSynonym = () => {
    const trimmed = newSynonym.trim();
    if (trimmed && !synonyms.includes(trimmed)) {
      setSynonyms([...synonyms, trimmed]);
      setNewSynonym("");
    }
  };

  const removeSynonym = (index: number) => {
    setSynonyms(synonyms.filter((_, i) => i !== index));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-moss-primary/10 rounded-lg">
              <Brain className="w-6 h-6 text-moss-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">AI Analyse Configuratie</h2>
              <p className="text-sm text-neutral-secondary-mid">
                Label: <span className="font-medium">{labelName}</span>
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Sluiten
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <SpinnerGap className="w-8 h-8 animate-spin text-moss-primary" />
            </div>
          ) : (
            <>
              {/* Synonyms / Alternative Names - Always visible */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Synoniemen & Zoekwoorden</CardTitle>
                  <CardDescription>
                    Alternatieve namen waarmee de AI dit documenttype kan herkennen.
                    Hoe meer synoniemen, hoe beter de classificatie.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Current synonyms */}
                  <div className="flex flex-wrap gap-2">
                    {synonyms.length === 0 ? (
                      <p className="text-sm text-neutral-secondary-mid italic">
                        Geen synoniemen - voeg er enkele toe voor betere herkenning
                      </p>
                    ) : (
                      synonyms.map((synonym, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-1 px-2 py-1 bg-moss-primary/10 text-moss-primary rounded-md text-sm"
                        >
                          <span>{synonym}</span>
                          <button
                            onClick={() => removeSynonym(index)}
                            className="hover:bg-moss-primary/20 rounded p-0.5"
                          >
                            <Trash className="w-3 h-3" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add new synonym */}
                  <div className="flex gap-2">
                    <Input
                      value={newSynonym}
                      onChange={(e) => setNewSynonym(e.target.value)}
                      placeholder="Nieuw synoniem toevoegen..."
                      className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addSynonym();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addSynonym}
                      disabled={!newSynonym.trim()}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Toevoegen
                    </Button>
                  </div>

                  <p className="text-xs text-neutral-secondary-mid">
                    Tip: Voeg variaties toe zoals afkortingen, alternatieve schrijfwijzen, en gerelateerde termen.
                    Bijv. voor "EPC Gebouw": EPC, energie certificaat, energieprestatie, energielabel
                  </p>
                </CardContent>
              </Card>

              {/* Document Description - Training instructions for AI */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Document Beschrijving (AI Training)</CardTitle>
                  <CardDescription>
                    Beschrijf dit documenttype zodat de AI het beter kan herkennen.
                    Leg uit wat het document IS, welke kenmerken het heeft, en waarmee het NIET verward moet worden.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Textarea
                    value={documentDescription}
                    onChange={(e) => setDocumentDescription(e.target.value)}
                    placeholder={`Voorbeeld voor "Elektriciteit keuringsattest":

Dit is een keuringsverslag voor elektrische installaties volgens AREI.

Kenmerken:
- Bevat meestal "AREI" of "Algemeen Reglement Elektrische Installaties"
- Heeft een keurder/inspecteur naam en erkenningsnummer
- Bevat een lijst van vaststellingen (inbreuken/opmerkingen)
- Heeft een eindconclusie: "conform" of "niet-conform"
- Vaak afgegeven door Vinçotte, BTV, AIB, SGS

NIET verwarren met:
- Elektrisch schema/plan (dat is een tekening)
- Factuur van elektricien (dat is een factuur)
- Offerte voor elektrische werken`}
                    rows={10}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-neutral-secondary-mid">
                    Hoe gedetailleerder de beschrijving, hoe beter de AI dit documenttype kan herkennen en onderscheiden van andere documenten.
                  </p>
                </CardContent>
              </Card>

              {/* Enable/Disable Toggle */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">AI Analyse Inschakelen</CardTitle>
                      <CardDescription>
                        Schakel automatische document analyse in voor dit label
                      </CardDescription>
                    </div>
                    <Switch
                      checked={aiAnalysisEnabled}
                      onCheckedChange={setAiAnalysisEnabled}
                    />
                  </div>
                </CardHeader>
              </Card>

              {aiAnalysisEnabled && (
                <>
                  {/* AI Provider & Model Selection */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">AI Provider & Model</CardTitle>
                      <CardDescription>
                        Kies welke AI provider en model gebruikt wordt voor analyse
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Provider</Label>
                          <Select
                            value={aiProvider}
                            onValueChange={(value: AIProvider) => {
                              setAiProvider(value);
                              // Reset model to first option of new provider
                              setAiModel(AI_MODELS[value][0].value);
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecteer provider" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={AIProvider.OPENAI}>OpenAI</SelectItem>
                              <SelectItem value={AIProvider.CLAUDE}>Anthropic Claude</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Model</Label>
                          <Select value={aiModel} onValueChange={setAiModel}>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecteer model" />
                            </SelectTrigger>
                            <SelectContent>
                              {AI_MODELS[aiProvider].map((model) => (
                                <SelectItem key={model.value} value={model.value}>
                                  {model.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Extraction Prompt */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Extractie Instructies</CardTitle>
                      <CardDescription>
                        Specifieke instructies voor de AI over hoe dit document geanalyseerd moet worden
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        placeholder={`Bijvoorbeeld:\nAnalyseer dit EPC document.\nLet specifiek op:\n- EPC score/label (A, B, C, D, E, F)\n- Uniek certificaatnummer\n- Datum van opmaak\n- Geldigheidsdatum`}
                        value={extractionPrompt}
                        onChange={(e) => setExtractionPrompt(e.target.value)}
                        rows={6}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-neutral-secondary-mid mt-2">
                        Tip: Wees specifiek over welke informatie belangrijk is voor dit documenttype
                      </p>
                    </CardContent>
                  </Card>

                  {/* Extraction Fields */}
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base">Velden om te Extraheren</CardTitle>
                          <CardDescription>
                            Definieer welke specifieke velden de AI moet extraheren
                          </CardDescription>
                        </div>
                        <Button variant="outline" size="sm" onClick={addExtractionField}>
                          <Plus className="w-4 h-4 mr-1" />
                          Veld Toevoegen
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {extractionFields.length === 0 ? (
                        <div className="text-center py-8 text-neutral-secondary-mid">
                          <Warning className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>Geen extractie velden gedefinieerd</p>
                          <p className="text-xs mt-1">
                            De AI zal standaard velden extraheren (datum, vervaldatum, status, opmerkingen)
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {extractionFields.map((field, index) => (
                            <div
                              key={index}
                              className="grid grid-cols-12 gap-2 items-start p-3 bg-neutral-50 rounded-lg"
                            >
                              <div className="col-span-2">
                                <Label className="text-xs">Sleutel</Label>
                                <Input
                                  value={field.key}
                                  onChange={(e) =>
                                    updateExtractionField(index, { key: e.target.value })
                                  }
                                  placeholder="score"
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div className="col-span-3">
                                <Label className="text-xs">Label</Label>
                                <Input
                                  value={field.label}
                                  onChange={(e) =>
                                    updateExtractionField(index, { label: e.target.value })
                                  }
                                  placeholder="EPC Score"
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div className="col-span-2">
                                <Label className="text-xs">Type</Label>
                                <Select
                                  value={field.type}
                                  onValueChange={(value: ExtractionField["type"]) =>
                                    updateExtractionField(index, { type: value })
                                  }
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {FIELD_TYPES.map((type) => (
                                      <SelectItem key={type.value} value={type.value}>
                                        {type.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-3">
                                <Label className="text-xs">Hint voor AI</Label>
                                <Input
                                  value={field.extractionHint || ""}
                                  onChange={(e) =>
                                    updateExtractionField(index, {
                                      extractionHint: e.target.value,
                                    })
                                  }
                                  placeholder="A, B, C, D, E of F"
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div className="col-span-1 flex items-center pt-5">
                                <label className="flex items-center gap-1 text-xs cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={field.required}
                                    onChange={(e) =>
                                      updateExtractionField(index, {
                                        required: e.target.checked,
                                      })
                                    }
                                    className="rounded"
                                  />
                                  Vereist
                                </label>
                              </div>
                              <div className="col-span-1 flex items-center pt-5 justify-end">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeExtractionField(index)}
                                  className="h-8 w-8 p-0 text-brick-primary hover:bg-brick-primary/10"
                                >
                                  <Trash className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Info about rules */}
                  <Card className="border-dashed">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base text-neutral-secondary-mid">
                        Geldigheids- en Conformiteitsregels
                      </CardTitle>
                      <CardDescription>
                        Regels voor het bepalen van geldigheid (verlopen?) en conformiteit (voldoet aan
                        eisen?) worden in een toekomstige versie configureerbaar. Momenteel worden
                        standaard regels toegepast:
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="font-medium mb-2">Geldigheid (standaard):</p>
                          <ul className="list-disc list-inside text-neutral-secondary-mid space-y-1">
                            <li>Vervaldatum moet in de toekomst liggen</li>
                          </ul>
                        </div>
                        <div>
                          <p className="font-medium mb-2">Conformiteit (standaard):</p>
                          <ul className="list-disc list-inside text-neutral-secondary-mid space-y-1">
                            <li>Geen blokkerende opmerkingen</li>
                            <li>Status niet "afgekeurd" of "niet conform"</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-neutral-50 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Annuleren
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isLoading}>
            {isSaving ? (
              <>
                <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
                Opslaan...
              </>
            ) : (
              <>
                <FloppyDisk className="w-4 h-4 mr-2" />
                Opslaan
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LabelAnalysisConfigPanel;
